import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Toaster } from "./components/ui/sonner";
import LoginPage from "./pages/LoginPage";
import ClientDashboard from "./pages/ClientDashboard";
import WelcomePage from "./pages/WelcomePage";
import MatterDetail from "./pages/MatterDetail";
import ContactPage from "./pages/ContactPage";
import AdminDashboard from "./pages/AdminDashboard";
import AdminClients from "./pages/AdminClients";
import AdminMonitoring from "./pages/AdminMonitoring";
import AdminPlanCoverage from "./pages/AdminPlanCoverage";
import AdminMatters from "./pages/AdminMatters";
import AdminHistory from "./pages/AdminHistory";
import AdminTeam from "./pages/AdminTeam";
import EmailsPage from "./pages/EmailsPage";
import DrivePage from "./pages/DrivePage";
import ProtectionPlanPage from "./pages/ProtectionPlanPage";
import BookCallPage from "./pages/BookCallPage";
import WhatsNewPage from "./pages/WhatsNewPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import GetStartedPage from "./pages/GetStartedPage";
import SettingsPage from "./pages/SettingsPage";
import "./App.css";

// Protected Route Component
function ProtectedRoute({ children, requiredRole }) {
  const { user, loading, previewMode } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-2 border-[#FFD60A] border-t-transparent rounded-full animate-spin"></div>
          <p className="text-[#52525B] text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // In preview mode, allow admin to view client routes
  if (previewMode && requiredRole === 'client') {
    return children;
  }

  if (requiredRole && user.role !== requiredRole) {
    return <Navigate to={user.role === 'admin' ? '/admin' : '/dashboard'} replace />;
  }

  return children;
}

// Public Route (redirect if already logged in)
function PublicRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-2 border-[#FFD60A] border-t-transparent rounded-full animate-spin"></div>
          <p className="text-[#52525B] text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  if (user) {
    return <Navigate to={user.role === 'admin' ? '/admin' : '/dashboard'} replace />;
  }

  return children;
}

const BASE_TITLE = "Indie Law | Client Portal";
const TITLES = {
  "/login": "Sign In · Indie Law",
  "/reset-password": "Reset Password · Indie Law",
  "/get-started": "Get Started · Indie Law",
  "/welcome": "Welcome · Indie Law",
  "/dashboard": "My Matters · Indie Law",
  "/emails": "Emails · Indie Law",
  "/drive": "Documents · Indie Law",
  "/protection-plan": "Protection Plan · Indie Law",
  "/book-call": "Book a Call · Indie Law",
  "/whats-new": "What's New · Indie Law",
  "/settings": "Settings · Indie Law",
  "/admin": "Admin · Indie Law",
  "/admin/clients": "Clients · Indie Law Admin",
  "/admin/matters": "Matters · Indie Law Admin",
  "/admin/history": "History · Indie Law Admin",
  "/admin/team": "Team · Indie Law Admin",
  "/admin/emails": "Emails · Indie Law Admin",
  "/admin/drive": "Documents · Indie Law Admin",
  "/admin/settings": "Settings · Indie Law Admin",
};

// Keeps the browser tab title in sync with the current route.
function PageTitle() {
  const { pathname } = useLocation();
  useEffect(() => {
    let title = TITLES[pathname];
    if (!title && pathname.startsWith("/matters/")) title = "Matter Details · Indie Law";
    document.title = title || BASE_TITLE;
  }, [pathname]);
  return null;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={
        <PublicRoute>
          <LoginPage />
        </PublicRoute>
      } />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/get-started" element={<GetStartedPage />} />
      
      {/* Client Routes */}
      <Route path="/welcome" element={
        <ProtectedRoute requiredRole="client">
          <WelcomePage />
        </ProtectedRoute>
      } />
      <Route path="/dashboard" element={
        <ProtectedRoute requiredRole="client">
          <ClientDashboard />
        </ProtectedRoute>
      } />
      <Route path="/matters/:matterId" element={
        <ProtectedRoute requiredRole="client">
          <MatterDetail />
        </ProtectedRoute>
      } />
      
      {/* Admin Routes */}
      <Route path="/admin" element={
        <ProtectedRoute requiredRole="admin">
          <AdminDashboard />
        </ProtectedRoute>
      } />
      <Route path="/admin/clients" element={
        <ProtectedRoute requiredRole="admin">
          <AdminClients />
        </ProtectedRoute>
      } />
      <Route path="/admin/matters" element={
        <ProtectedRoute requiredRole="admin">
          <AdminMatters />
        </ProtectedRoute>
      } />
      <Route path="/admin/monitoring" element={
        <ProtectedRoute requiredRole="admin">
          <AdminMonitoring />
        </ProtectedRoute>
      } />
      <Route path="/admin/coverage" element={
        <ProtectedRoute requiredRole="admin">
          <AdminPlanCoverage />
        </ProtectedRoute>
      } />
      <Route path="/admin/history" element={
        <ProtectedRoute requiredRole="admin">
          <AdminHistory />
        </ProtectedRoute>
      } />
      <Route path="/admin/team" element={
        <ProtectedRoute requiredRole="admin">
          <AdminTeam />
        </ProtectedRoute>
      } />
      <Route path="/admin/emails" element={
        <ProtectedRoute requiredRole="admin">
          <EmailsPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/drive" element={
        <ProtectedRoute requiredRole="admin">
          <DrivePage />
        </ProtectedRoute>
      } />
      <Route path="/admin/settings" element={
        <ProtectedRoute requiredRole="admin">
          <SettingsPage />
        </ProtectedRoute>
      } />
      
      {/* Client Extra Routes */}
      <Route path="/emails" element={
        <ProtectedRoute requiredRole="client">
          <EmailsPage />
        </ProtectedRoute>
      } />
      <Route path="/drive" element={
        <ProtectedRoute requiredRole="client">
          <DrivePage />
        </ProtectedRoute>
      } />
      <Route path="/protection-plan" element={
        <ProtectedRoute requiredRole="client">
          <ProtectionPlanPage />
        </ProtectedRoute>
      } />
      <Route path="/book-call" element={
        <ProtectedRoute requiredRole="client">
          <BookCallPage />
        </ProtectedRoute>
      } />
      <Route path="/whats-new" element={
        <ProtectedRoute requiredRole="client">
          <WhatsNewPage />
        </ProtectedRoute>
      } />
      <Route path="/settings" element={
        <ProtectedRoute requiredRole="client">
          <SettingsPage />
        </ProtectedRoute>
      } />
      
      {/* Default redirect */}
      <Route path="/" element={<Navigate to="/login" replace />} />
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <PageTitle />
        <AppRoutes />
        <Toaster position="top-right" />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
